import React, { createContext, useContext, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LangContext = createContext();
const STORAGE_KEY = "dosmass_lang";

const isValid = (l) => l === "en" || l === "es";

function readLangFromSearch(search) {
  const v = new URLSearchParams(search).get("lang");
  return isValid(v) ? v : null;
}

/**
 * Single source of truth: the URL ?lang= parameter.
 * Reading order on first paint:  URL > localStorage > "es"
 * Writing: setLang/toggle update the URL via react-router navigate(replace),
 * which automatically re-renders all consumers — no React state, no race
 * with the URL-watch effect.
 */
export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive current language directly from the URL on every render.
  const urlLang = readLangFromSearch(location.search);
  const fallbackLang = (() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return isValid(saved) ? saved : "es";
  })();
  const lang = urlLang || fallbackLang;

  // If the URL has no ?lang= yet, write the resolved value back into it once
  // so deep links + sharing behave consistently.
  useEffect(() => {
    if (!urlLang) {
      const params = new URLSearchParams(location.search);
      params.set("lang", lang);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash },
        { replace: true }
      );
    }
  }, [urlLang, lang, location.pathname, location.search, location.hash, navigate]);

  // Persist + reflect to <html lang="...">
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback(
    (l) => {
      if (!isValid(l)) return;
      const params = new URLSearchParams(location.search);
      params.set("lang", l);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash },
        { replace: true }
      );
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  const toggle = useCallback(
    () => setLang(lang === "es" ? "en" : "es"),
    [lang, setLang]
  );

  return (
    <LangContext.Provider value={{ lang, setLang, toggle }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
